import { useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  COMPANY_TARGET,
  bulkIssueResponseSchema,
  catalogResponseSchema,
  orgRosterResponseSchema,
  rosterResponseSchema,
  unitsResponseSchema,
  type BulkIssueResponse,
} from '@unit/shared';
import { useApi } from '../../lib/useApi.js';
import { apiPost } from '../../lib/api.js';
import { MissionSelect } from '../missions/MissionSelect.js';
import { useAction } from '../../lib/useAction.js';
import { useClaimRoles } from '../auth/useClaimRoles.js';
import { ApiError } from '../../components/ApiError.js';
import { ActionError } from '../../components/ActionError.js';
import { STOREROOM_ROLES } from '../../components/RequireRole.js';
import { itemName } from '../inventory/itemName.js';
import { LineEditor, type Line } from './LineEditor.js';
import { TemplateSelect } from './TemplateSelect.js';
import { PersonName } from '../../components/PersonName.js';

const input =
  'rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-900 focus:border-slate-500 focus:outline-none';
const primary =
  'rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-700 disabled:opacity-40';

/**
 * Bulk issue (§3.5; owner 2026-09-13: to a team, a platoon, or the whole
 * company): the same items to every active soldier in the target, one form
 * per soldier, reserved together or not at all. Serialized items are
 * assigned one unit per soldier by the server, in serial order. Opened from
 * a roster the target is preselected; opened bare, it is picked here; opened
 * from a grid selection (router state, see `BulkPick`) the target is exactly
 * those people and the picker is replaced by their names.
 */

/** What the org grid hands over in `location.state` for "issue to the selected". */
export interface BulkPick {
  soldierUids: string[];
}

function pickFrom(state: unknown): string[] {
  const uids = (state as Partial<BulkPick> | null)?.soldierUids;
  return Array.isArray(uids) ? uids.filter((u): u is string => typeof u === 'string' && u.length > 0) : [];
}
export function BulkIssuePage() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const { state } = useLocation();
  const { roles } = useClaimRoles();
  const storeroom = roles.some((r) => STOREROOM_ROLES.includes(r));
  const picked = useMemo(() => pickFrom(state), [state]);
  const [target, setTarget] = useState(picked.length > 0 ? '' : (params.get('unit') ?? ''));

  const units = useApi('/units', unitsResponseSchema);
  const roster = useApi(target ? `/units/${target}/soldiers` : null, rosterResponseSchema);
  const orgRoster = useApi(picked.length > 0 ? '/units/roster' : null, orgRosterResponseSchema);
  const catalog = useApi('/catalog', catalogResponseSchema);
  const [lines, setLines] = useState<Line[]>([]);
  const [result, setResult] = useState<BulkIssueResponse | null>(null);
  const [missionId, setMissionId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const submit = useAction((body: unknown) => apiPost('/issuance/bulk', bulkIssueResponseSchema, body));

  const items = useMemo(() => (catalog.kind === 'ready' ? catalog.data.items.filter((i) => i.active) : []), [catalog]);
  const unit = units.kind === 'ready' ? units.data.units.find((u) => u.id === target) : undefined;
  const targetName = target === COMPANY_TARGET ? t('bulk.company') : (unit?.name ?? target);
  const pickedRows = useMemo(() => {
    if (orgRoster.kind !== 'ready') return [];
    const byUid = new Map(orgRoster.data.rows.map((r) => [r.uid, r]));
    return picked.map((uid) => byUid.get(uid)).filter((r): r is NonNullable<typeof r> => !!r);
  }, [orgRoster, picked]);
  const soldiers =
    picked.length > 0
      ? pickedRows.filter((s) => s.status === 'active')
      : roster.kind === 'ready'
        ? roster.data.soldiers.filter((s) => s.status === 'active')
        : [];
  const hasTarget = picked.length > 0 || !!target;

  // The units this caller may bulk-issue to: the server decides, the list
  // just does not offer what would be refused.
  const targets = useMemo(() => {
    if (units.kind !== 'ready') return [];
    const viewable = new Set(units.data.viewableUnitIds);
    const byId = new Map(units.data.units.map((u) => [u.id, u]));
    return units.data.units
      .filter((u) => u.active && viewable.has(u.id))
      .sort((a, b) => {
        const pa = a.type === 'platoon' ? a.order : (byId.get(a.parentId ?? '')?.order ?? 0);
        const pb = b.type === 'platoon' ? b.order : (byId.get(b.parentId ?? '')?.order ?? 0);
        return pa - pb || (a.type === 'platoon' ? -1 : 1) - (b.type === 'platoon' ? -1 : 1) || a.order - b.order;
      })
      .map((u) => ({ id: u.id, label: u.type === 'team' ? `${byId.get(u.parentId ?? '')?.name ?? ''} · ${u.name}` : u.name }));
  }, [units]);

  const send = async () => {
    const r = await submit.run({
      ...(picked.length > 0 ? { soldierUids: picked } : { unitId: target }),
      lines: lines.map((l) => ({ catalogItemId: l.item.id, quantity: l.item.tracking === 'serialized' ? 1 : l.quantity, condition: l.condition, notes: l.notes.trim() || null })),
      missionId: missionId || null,
      dueDate: dueDate ? new Date(dueDate).toISOString() : null,
    });
    if (r) {
      setResult(r);
      setLines([]);
    }
  };

  if (roster.kind === 'error') return <ApiError code={roster.code} requestId={roster.requestId} />;
  if (orgRoster.kind === 'error') return <ApiError code={orgRoster.code} requestId={orgRoster.requestId} />;

  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200" data-testid="bulk-page">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-900">
          {picked.length > 0 ? t('bulk.pickedTitle', { count: picked.length }) : target ? t('bulk.title', { unit: targetName }) : t('bulk.pickTitle')}
        </h2>
        <Link to={target && target !== COMPANY_TARGET ? `/org/${target}` : '/org'} className="text-sm text-slate-500 underline-offset-4 hover:underline">
          {t('org.backToTree')}
        </Link>
      </div>

      {picked.length > 0 ? (
        <ul className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-sm text-slate-700" data-testid="bulk-picked">
          {pickedRows.map((s) => (
            <li key={s.uid} className={s.status === 'active' ? '' : 'line-through opacity-60'}>
              <PersonName person={s} />
            </li>
          ))}
        </ul>
      ) : (
      <label className="mt-3 block text-xs text-slate-600">
        {t('bulk.target')}
        <select
          className={`${input} mt-1 block`}
          value={target}
          onChange={(e) => {
            setTarget(e.target.value);
            setResult(null);
          }}
          data-testid="bulk-target"
        >
          <option value="">{t('bulk.chooseTarget')}</option>
          {storeroom && <option value={COMPANY_TARGET}>{t('bulk.company')}</option>}
          {targets.map((u) => (
            <option key={u.id} value={u.id}>{u.label}</option>
          ))}
        </select>
      </label>
      )}

      {hasTarget && (
        <p className="mt-2 text-sm text-slate-500" data-testid="bulk-count">
          {t(picked.length > 0 ? 'bulk.pickedHint' : 'bulk.hint', { count: soldiers.length })}
        </p>
      )}

      {result ? (
        <div className="mt-4 rounded-xl bg-emerald-50 p-4 ring-1 ring-emerald-200" data-testid="bulk-done">
          <p className="font-medium text-emerald-900">{t('bulk.done', { count: result.forms.length })}</p>
          <ul className="mt-2 divide-y divide-emerald-100 text-sm">
            {result.forms.map((f) => (
              <li key={f.id} className="py-1.5">
                <Link to={`/forms/${f.id}`} className="underline-offset-4 hover:underline">{f.soldierName}</Link>
                <span className="ms-2 text-emerald-800">{t('forms.itemCount', { count: f.lineItems.length })}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <>
          <div className="mt-3">
            <TemplateSelect items={items} onPick={setLines} />
          </div>
          <LineEditor lines={lines} onChange={setLines} items={items} withSerials={false} testId="bulk" />
          {lines.length > 0 && soldiers.length > 0 && (
            <ul className="mt-2 text-xs text-slate-500" data-testid="bulk-totals">
              {lines.map((line) => (
                <li key={line.key}>
                  {itemName(line.item, i18n.language)}:{' '}
                  {line.item.tracking === 'serialized'
                    ? t('bulk.serializedNote', { count: soldiers.length })
                    : t('bulk.quantityNote', { count: line.quantity * soldiers.length })}
                </li>
              ))}
            </ul>
          )}
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <MissionSelect
              testId="bulk-mission-select"
              value={missionId}
              onChange={(id, endAt) => {
                setMissionId(id);
                if (endAt && !dueDate) setDueDate(endAt.slice(0, 10));
              }}
            />
            <label className="text-xs text-slate-600">
              {t('issue.dueDate')}
              <input type="date" className={`${input} latin-inline mt-1 block`} value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="bulk-due-date" />
            </label>
          </div>
          <button
            type="button"
            onClick={() => void send()}
            disabled={!hasTarget || lines.length === 0 || soldiers.length === 0 || submit.busy}
            className={`${primary} mt-4`}
            data-testid="bulk-submit"
          >
            {submit.busy ? t('issue.submitting') : t('bulk.submit', { count: soldiers.length })}
          </button>
          <div className="mt-3">
            <ActionError error={submit.error} />
            {(submit.error?.details.reason === 'insufficient_stock' || submit.error?.details.reason === 'insufficient_units') && (
              <p className="text-sm text-slate-600">
                {t('issue.insufficient', { available: Number(submit.error.details.available), requested: Number(submit.error.details.requested) })}
              </p>
            )}
            {submit.error?.details.reason === 'too_many_writes' && (
              <p className="text-sm text-slate-600">
                {t('bulk.tooManyWrites', { soldiers: Number(submit.error.details.soldiers), writes: Number(submit.error.details.writes), max: Number(submit.error.details.max) })}
              </p>
            )}
          </div>
        </>
      )}
    </section>
  );
}
